// @ts-nocheck
import React, { useState, useMemo, useCallback, useEffect } from 'react';

/**
 * DistributeModal — Two-step modal:
 * 1) Pick a store attribute to distribute by (e.g. Volume Class)
 * 2) Enter qty per distinct value (A=12, B=6, C=0, D=0)
 *
 * If total exceeds availability, shows overflow resolution screen.
 */

interface DistributeModalProps {
	open: boolean;
	onClose: () => void;
	skuOrderData: Record<string, any>[];
	allSkuData?: Record<string, any>[];
	skuFieldName: string;
	skuId: string;
	initialAvail: number;
	shelfQtyField?: string;
	onApply: (qtyMap: Record<string, number>) => void;
	storeColumns: { columnName: string; displayName: string }[];
}

type Step = 'pick-column' | 'enter-values' | 'overflow';

const DistributeModal: React.FC<DistributeModalProps> = ({
	open,
	onClose,
	skuOrderData,
	allSkuData,
	skuFieldName,
	skuId,
	initialAvail,
	shelfQtyField,
	onApply,
	storeColumns,
}) => {
	const [step, setStep] = useState<Step>('pick-column');
	const [selectedCol, setSelectedCol] = useState('');
	const [valueQtys, setValueQtys] = useState<Record<string, number>>({});
	const [overflowAction, setOverflowAction] = useState<'cap' | 'proportional' | 'cancel'>('proportional');

	// Reset when opened
	useEffect(() => {
		if (open) {
			setStep('pick-column');
			setSelectedCol('');
			setValueQtys({});
		}
	}, [open]);

	// Show all store attribute columns that exist in the order data and have at least 2 distinct values
	// Use allSkuData (unfiltered) for discovery so store/WH filters don't hide attributes
	const groupableColumns = useMemo(() => {
		const discoveryData = allSkuData?.length ? allSkuData : skuOrderData;
		if (!discoveryData.length || !storeColumns.length) return [];
		const dataKeys = new Set(Object.keys(discoveryData[0]));
		return storeColumns
			.filter((col) => dataKeys.has(col.columnName))
			.map((col) => {
				const distinct = [
					...new Set(discoveryData.map((r) => String(r[col.columnName] || '')).filter(Boolean)),
				].sort();
				return {
					key: col.columnName,
					label: col.displayName,
					distinctCount: distinct.length,
					values: distinct,
				};
			})
			.filter((col) => col.distinctCount >= 2);
	}, [allSkuData, skuOrderData, storeColumns]);

	const selectedColData = useMemo(
		() => groupableColumns.find((c) => c.key === selectedCol),
		[groupableColumns, selectedCol]
	);

	// Init value qtys when column is selected
	const handleSelectColumn = useCallback(
		(colKey: string) => {
			setSelectedCol(colKey);
			const col = groupableColumns.find((c) => c.key === colKey);
			if (col) {
				const init: Record<string, number> = {};
				col.values.forEach((v) => {
					init[v] = 0;
				});
				setValueQtys(init);
			}
			setStep('enter-values');
		},
		[groupableColumns]
	);

	// Calculate totals
	const totals = useMemo(() => {
		if (!selectedColData) return { totalQty: 0, storeBreakdown: {} };
		let total = 0;
		const breakdown: Record<string, number> = {};
		selectedColData.values.forEach((val) => {
			const qty = valueQtys[val] || 0;
			const matchingStores = skuOrderData.filter((r) => String(r[selectedCol]) === val).length;
			const groupTotal = qty * matchingStores;
			total += groupTotal;
			breakdown[val] = matchingStores;
		});
		return { totalQty: total, storeBreakdown: breakdown };
	}, [valueQtys, selectedColData, skuOrderData, selectedCol]);

	const isOverAvail = totals.totalQty > initialAvail;

	const handleApply = useCallback(() => {
		if (isOverAvail) {
			setStep('overflow');
			return;
		}
		doApply(valueQtys);
	}, [isOverAvail, valueQtys]);

	const doApply = useCallback(
		(qtys: Record<string, number>) => {
			// Build the update: for each row, find its group value and set order qty with per-row snapping
			const storeKey = Object.keys(skuOrderData[0] || {}).find((k) => /^store$/i.test(k)) || 'Store';
			const result: Record<string, number> = {};

			skuOrderData.forEach((row) => {
				const groupVal = String(row[selectedCol] || '');
				const store = String(row[storeKey] || '');
				const step = shelfQtyField ? (Number(row[shelfQtyField] || 0) > 1 ? Number(row[shelfQtyField]) : 1) : 1;
				let qty = qtys[groupVal] || 0;
				qty = qty > 0 ? Math.ceil(qty / step) * step : 0;
				if (qty < 0) qty = 0;
				result[store] = qty;
			});

			onApply(result);
			onClose();
		},
		[skuOrderData, selectedCol, shelfQtyField, onApply, onClose]
	);

	const handleOverflowApply = useCallback(() => {
		if (overflowAction === 'cancel') {
			onClose();
			return;
		}

		const adjustedQtys = { ...valueQtys };

		if (overflowAction === 'proportional') {
			// Scale all values proportionally to fit within availability
			const ratio = initialAvail / totals.totalQty;
			Object.keys(adjustedQtys).forEach((val) => {
				adjustedQtys[val] = Math.floor(adjustedQtys[val] * ratio);
			});
		} else if (overflowAction === 'cap') {
			// Reduce from lowest-priority groups first (reverse alphabetical)
			let remaining = initialAvail;
			const sortedVals = Object.keys(adjustedQtys).sort(); // A first = highest priority
			sortedVals.forEach((val) => {
				const matchingStores = skuOrderData.filter((r) => String(r[selectedCol]) === val).length;
				const groupNeed = adjustedQtys[val] * matchingStores;
				if (remaining >= groupNeed) {
					remaining -= groupNeed;
				} else {
					const maxPerStore = Math.floor(remaining / matchingStores);
					adjustedQtys[val] = Math.max(0, maxPerStore);
					remaining -= maxPerStore * matchingStores;
				}
			});
		}

		// Per-row snapping happens inside doApply
		doApply(adjustedQtys);
	}, [overflowAction, valueQtys, totals, initialAvail, skuOrderData, selectedCol, doApply, onClose]);

	if (!open) return null;

	return (
		<div className="obs-modal-overlay" onClick={onClose}>
			<div className="obs-modal" onClick={(e) => e.stopPropagation()}>
				{/* Header */}
				<div className="obs-modal__header">
					<span className="obs-modal__title">
						{step === 'pick-column' && 'Distribute by Attribute'}
						{step === 'enter-values' && `Distribute by ${selectedColData?.label || selectedCol}`}
						{step === 'overflow' && 'Availability Exceeded'}
					</span>
					<button className="obs-modal__close" onClick={onClose}>
						✕
					</button>
				</div>

				{/* Step 1: Pick column */}
				{step === 'pick-column' && (
					<div className="obs-modal__body">
						<div className="obs-modal__subtitle">Select a store attribute to distribute by:</div>
						<div className="obs-modal__col-list">
							{groupableColumns.map((col) => (
								<button
									key={col.key}
									className="obs-modal__col-btn"
									onClick={() => handleSelectColumn(col.key)}
								>
									<span className="obs-modal__col-name">{col.label}</span>
									<span className="obs-modal__col-count">{col.distinctCount} values</span>
								</button>
							))}
							{groupableColumns.length === 0 && (
								<div className="obs-modal__empty">
									No store attributes found with 2–20 distinct values.
								</div>
							)}
						</div>
					</div>
				)}

				{/* Step 2: Enter values */}
				{step === 'enter-values' && selectedColData && (
					<div className="obs-modal__body">
						<div className="obs-modal__subtitle">
							Enter qty per store for each {selectedColData.label} value:
							{shelfQtyField && <span className="obs-modal__shelf-note"> (snaps to each store's shelf qty)</span>}
						</div>
						<div className="obs-modal__val-grid">
							<div className="obs-modal__val-hdr">
								<span>{selectedColData.label}</span>
								<span>Stores</span>
								<span>Qty/Store</span>
								<span>Subtotal</span>
							</div>
							{selectedColData.values.map((val) => {
								const storeCount = totals.storeBreakdown[val] || 0;
								const qty = valueQtys[val] || 0;
								const sub = qty * storeCount;
								return (
									<div key={val} className="obs-modal__val-row">
										<span className="obs-modal__val-label">{val}</span>
										<span className="obs-modal__val-stores">{storeCount}</span>
										<input
											className="obs-modal__val-input"
											type="number"
											min={0}
											step={1}
											value={qty}
											onChange={(e) =>
												setValueQtys((prev) => ({ ...prev, [val]: Number(e.target.value) }))
											}
										/>
										<span
											className={`obs-modal__val-sub ${sub > 0 ? 'obs-modal__val-sub--active' : ''}`}
										>
											{sub.toLocaleString()}
										</span>
									</div>
								);
							})}
						</div>
						<div className="obs-modal__summary">
							<div className="obs-modal__summary-row">
								<span>Total Order Qty:</span>
								<span className={isOverAvail ? 'obs-modal__over' : ''}>
									{totals.totalQty.toLocaleString()}
								</span>
							</div>
							<div className="obs-modal__summary-row">
								<span>Available:</span>
								<span>{initialAvail.toLocaleString()}</span>
							</div>
							{isOverAvail && (
								<div className="obs-modal__summary-row obs-modal__over">
									<span>Over by:</span>
									<span>{(totals.totalQty - initialAvail).toLocaleString()}</span>
								</div>
							)}
						</div>
						<div className="obs-modal__actions">
							<button className="obs-modal__back-btn" onClick={() => setStep('pick-column')}>
								← Back
							</button>
							<button
								className="obs-modal__apply-btn"
								onClick={handleApply}
								disabled={totals.totalQty === 0}
							>
								{isOverAvail ? 'Review Overflow →' : 'Apply Distribution'}
							</button>
						</div>
					</div>
				)}

				{/* Step 3: Overflow resolution */}
				{step === 'overflow' && (
					<div className="obs-modal__body">
						<div className="obs-modal__overflow-banner">
							Total order ({totals.totalQty.toLocaleString()}) exceeds availability (
							{initialAvail.toLocaleString()}) by{' '}
							<strong>{(totals.totalQty - initialAvail).toLocaleString()}</strong> units.
						</div>
						<div className="obs-modal__subtitle">How would you like to resolve this?</div>
						<div className="obs-modal__overflow-options">
							<label
								className={`obs-modal__overflow-opt ${overflowAction === 'proportional' ? 'obs-modal__overflow-opt--sel' : ''}`}
							>
								<input
									type="radio"
									name="overflow"
									value="proportional"
									checked={overflowAction === 'proportional'}
									onChange={() => setOverflowAction('proportional')}
								/>
								<div>
									<div className="obs-modal__opt-title">Scale Proportionally</div>
									<div className="obs-modal__opt-desc">
										Reduce all groups proportionally to fit within {initialAvail.toLocaleString()}{' '}
										units.
									</div>
								</div>
							</label>
							<label
								className={`obs-modal__overflow-opt ${overflowAction === 'cap' ? 'obs-modal__overflow-opt--sel' : ''}`}
							>
								<input
									type="radio"
									name="overflow"
									value="cap"
									checked={overflowAction === 'cap'}
									onChange={() => setOverflowAction('cap')}
								/>
								<div>
									<div className="obs-modal__opt-title">Priority Cap (A → Z)</div>
									<div className="obs-modal__opt-desc">
										Fill groups in order (A first, then B, etc.) until availability runs out. Later
										groups get reduced or zeroed.
									</div>
								</div>
							</label>
							<label
								className={`obs-modal__overflow-opt ${overflowAction === 'cancel' ? 'obs-modal__overflow-opt--sel' : ''}`}
							>
								<input
									type="radio"
									name="overflow"
									value="cancel"
									checked={overflowAction === 'cancel'}
									onChange={() => setOverflowAction('cancel')}
								/>
								<div>
									<div className="obs-modal__opt-title">Cancel</div>
									<div className="obs-modal__opt-desc">Go back and adjust quantities manually.</div>
								</div>
							</label>
						</div>
						<div className="obs-modal__actions">
							<button className="obs-modal__back-btn" onClick={() => setStep('enter-values')}>
								← Back
							</button>
							<button className="obs-modal__apply-btn" onClick={handleOverflowApply}>
								{overflowAction === 'cancel' ? 'Cancel' : 'Apply Resolution'}
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default DistributeModal;
